// libs
import { useCallback, useContext, useEffect, useRef, useState } from "react";

// internal-deps
import { useMountedRef, captureError } from "@swiggy-private/core-ui";
import {
    useSessionStorage,
    ApiContext,
    PublicApiContext,
    CHECKOUT_WEB_PUBLIC_ENDPOINT_KEYS,
} from "@swiggy-private/checkout-core";
import { isEmpty } from "@swiggy-private/payment-core";

// interfaces
import { GetPaymentMethodsResponse } from "@payment-client/interfaces/PaymentApi";
import { JuspayClientAuthTokenData, JuspaySdkFeatureAvailable } from "@payment-client/interfaces/Juspay";

// helpers
import { isJuspaySdkAvailable, resolveClientAuthTokenDataForMerchantId } from "@payment-client/helpers/JuspayHelpers";
import { isBusinessLineSwiggyMoney } from "@payment-client/helpers/Misc";

// apis
import { getJuspayClientAuthToken } from "@payment-client/apis/Juspay";

// constants
import { DEFAULT_JUSPAY_SDK_STATE, SWIGGY_MONEY_JUSPAY_SDK_STATE } from "@payment-client/constants/StorageKeys";

// contexts
import { JuspayContextValue } from "@payment-client/contexts/JuspayContext";
import { isPublicApiEnabled } from "@swiggy-private/core-utils";
import { getPublicJuspayClientAuthToken } from "@payment-client/apis/publicJuspay";
import { getPublicJuspayClientAuthTokensV3 } from "@payment-client/apis/publicJuspayV3";

export interface JuspaySdkState {
    isSdkAvailable?: boolean;
    clientAuthTokenData?: JuspayClientAuthTokenData | Record<string, JuspayClientAuthTokenData[]>;
    featuresAvailability?: JuspaySdkFeatureAvailable;
}

export const FETCH_TOKEN_ERROR = "Received empty data while fetching juspay client auth token";

export const useJuspaySdkState = (
    tenant: string,
    merchantId?: GetPaymentMethodsResponse["juspayMeta"]["merchantId"],
    // this optional param needs to be passed to iOS when native UPI or Cred instruments exist
    forceEnableSDK?: boolean,
    /** When true, uses the V3 multi-tenant auth tokens API; falls back to V2 when false */
    isV3Enabled?: boolean,
): JuspayContextValue => {
    const isMountedRef = useMountedRef();
    const juspayStateKey = isBusinessLineSwiggyMoney() ? SWIGGY_MONEY_JUSPAY_SDK_STATE : DEFAULT_JUSPAY_SDK_STATE;
    const [state, setState] = useSessionStorage<JuspaySdkState>(juspayStateKey, {});
    const [isInitialiseSdkAttempted, setInitialiseSdkAttempted] = useState(false);
    const apiContextValue = useContext(ApiContext);
    const apiHost = apiContextValue.host.unwrap();
    const fetchRef = useRef(false);
    const [fetchingJuspaySdk, setFetchingJuspaySdk] = useState(false);
    const publicApiConfig = useContext(PublicApiContext);

    const isJuspayAuthTokenApiPublic = isPublicApiEnabled(
        publicApiConfig || {},
        CHECKOUT_WEB_PUBLIC_ENDPOINT_KEYS.PAYMENT_APIS,
        CHECKOUT_WEB_PUBLIC_ENDPOINT_KEYS.JUSPAY_CLIENT_TOKEN,
    );

    const getJuspayClientAuthTokenFn = isJuspayAuthTokenApiPublic
        ? getPublicJuspayClientAuthToken
        : getJuspayClientAuthToken;

    /** Fetches raw auth tokens from V3 (or V2). No SDK token selection — just returns the map and tenant token. */
    const fetchAuthTokens = useCallback(async (): Promise<
        JuspayClientAuthTokenData | Record<string, JuspayClientAuthTokenData[]>
    > => {
        if (isV3Enabled) {
            const allAuthTokens = await getPublicJuspayClientAuthTokensV3({ apiHost, tenants: tenant });
            return allAuthTokens;
        }
        const clientAuthTokenData = await getJuspayClientAuthTokenFn({ apiHost, tenant });
        return clientAuthTokenData;
    }, [apiHost, getJuspayClientAuthTokenFn, isV3Enabled, tenant]);

    const checkIfSDKAvailable = useCallback(
        async (
            allAuthTokens: JuspayClientAuthTokenData | Record<string, JuspayClientAuthTokenData[]>,
        ): Promise<{
            isSdkAvailable: boolean;
            featuresAvailability?: JuspaySdkFeatureAvailable;
        }> => {
            const sdkTokenData = resolveClientAuthTokenDataForMerchantId(allAuthTokens, merchantId ?? "");

            return isJuspaySdkAvailable(sdkTokenData as JuspayClientAuthTokenData, merchantId, forceEnableSDK, tenant)
                .catch((err) => {
                    captureError(err);
                    return { isSdkAvailable: false };
                })
                .then((data) => {
                    return data;
                });
        },
        [forceEnableSDK, merchantId, tenant],
    );

    const initializeSdk = useCallback(async () => {
        fetchRef.current = true;
        setInitialiseSdkAttempted(true);

        try {
            setFetchingJuspaySdk(true);
            const clientAuthTokenData = await fetchAuthTokens();

            const { isSdkAvailable, featuresAvailability } = await checkIfSDKAvailable(clientAuthTokenData);
            setState({
                isSdkAvailable,
                clientAuthTokenData,
                featuresAvailability,
            });
            setFetchingJuspaySdk(false);
            fetchRef.current = false;
        } catch (err) {
            fetchRef.current = false;
            setFetchingJuspaySdk(false);
            captureError(err);
        }
        // disabled lint because setState of useSessionStorage should not be present
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [checkIfSDKAvailable, fetchAuthTokens]);

    const fetchJuspayClientAuthToken = useCallback(async () => {
        try {
            const clientAuthTokenData = await fetchAuthTokens();

            const { isSdkAvailable, featuresAvailability } = await checkIfSDKAvailable(clientAuthTokenData);
            setState({
                isSdkAvailable,
                clientAuthTokenData,
                featuresAvailability,
            });

            return clientAuthTokenData;
        } catch (err) {
            captureError(err);
        }
    }, [checkIfSDKAvailable, fetchAuthTokens]);

    useEffect(() => {
        if (!isMountedRef.current || fetchRef.current || !isEmpty(state) || isInitialiseSdkAttempted) {
            return;
        }
        initializeSdk();
    }, [initializeSdk, fetchRef, isMountedRef, state, isInitialiseSdkAttempted]);

    return { ...state, isSdkAvailable: !!state.isSdkAvailable, fetchingJuspaySdk, fetchJuspayClientAuthToken };
};
